import {
  Box, Grid2, IconButton, Typography, useTheme,
} from '@mui/material';
import {
  FiFacebook, FiInstagram, FiYoutube,
} from 'react-icons/fi';
import { SiTiktok } from 'react-icons/si';
import { DateTime } from 'luxon';
import React from 'react';
import { TypographyElement } from '@plugins/next-cms-core';
import useConsentManager from '@lib/hooks/useConsentManager';
import { useTranslation } from 'next-i18next';
import Link from '@components/atoms/Link';
import Image from '@components/atoms/Image2';
import BrandLogo from '@components/molecules/BrandLogo';
import Container from '@components/atoms/Container';
import FooterMenu from '../molecules/FooterMenu';

const basePadding = 80;
const gridBreakpoints = {
  xs: 12,
  sm: 6,
  md: 3,
  lg: 3,
};

export default function Footer({
  data,
  config,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { changeConsent } = useConsentManager();

  const { pageBottomConfig } = data ?? {};
  const { globalInfoText } = pageBottomConfig?.data?.attributes ?? {};
  // `?? []` rather than a destructuring default, so an explicit null is covered too
  const columns = pageBottomConfig?.data?.attributes?.columns ?? [];
  const { bottomInfoText } = config ?? {};

  const handleChangeConsent = (event) => {
    event.preventDefault();
    event.stopPropagation();

    changeConsent();
  };

  return (
    <Box
      sx={{
        contentVisibility: 'auto',
        containIntrinsicSize: '804px',
        background: theme.palette.dark.main,
        paddingTop: `${basePadding / 2}px`,
        paddingBottom: `${basePadding * 2}px`,
      }}
    >
      <Container>
        {(globalInfoText || bottomInfoText) && (
          <Box
            sx={{
              ...SEPARATOR_STYLE,
              color: theme.palette.dark.contrastText,
              opacity: 0.65,
              paddingBottom: theme.spacing(2),
              marginBottom: theme.spacing(5),
            }}
          >
            {bottomInfoText && (
              <TypographyElement
                color="inherit"
                data={{
                  displayVariant: 'caption',
                  value: bottomInfoText,
                }}
              />
            )}
            {globalInfoText && (
              <TypographyElement
                color="inherit"
                data={{
                  displayVariant: 'caption',
                  value: globalInfoText,
                }}
              />
            )}
          </Box>
        )}
        <Grid2 container spacing={4} sx={{ mb: 2 }}>
          <Grid2 size={gridBreakpoints}>
            <BrandLogo
              alt=""
              color="inverted"
              style={{
                width: 'auto',
                height: 58,
              }}
            />
            <Box mt={3}>
              <a
                href="https://www.provenexpert.com/de-de/csb-schimmel-automobile-gmbh/"
                rel="noreferrer"
                target="_blank"
                title="Kundenbewertungen &amp; Erfahrungen zu CSB Schimmel Automobile GmbH. Mehr Infos anzeigen."
              >
                <Image
                  alt="Erfahrungen &amp; Bewertungen zu CSB Schimmel Automobile GmbH"
                  data-name="provenexpert"
                  height={200}
                  loading="lazy"
                  src="https://images.provenexpert.com/2e/6e/66449c43267c6ff417912012d2b7/widget_square_200_de_0.png"
                  width={200}
                />
              </a>
            </Box>
            <Box mt={1}>
              <Link href="https://www.facebook.com/csb.berlin/" legacyBehavior>
                <IconButton
                  aria-label="Facebook"
                  color="secondary"
                  component="a"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                >
                  <FiFacebook />
                </IconButton>
              </Link>
              <Link href="https://www.youtube.com/channel/UC2vfkFLsmJEsHUuYkFp7UHA" legacyBehavior>
                <IconButton
                  aria-label="Youtube"
                  color="secondary"
                  component="a"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                >
                  <FiYoutube />
                </IconButton>
              </Link>
              <Link href="https://www.instagram.com/schimmelautomobile/" legacyBehavior>
                <IconButton
                  aria-label="Instagram"
                  color="secondary"
                  component="a"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                >
                  <FiInstagram />
                </IconButton>
              </Link>
              <Link href="https://www.tiktok.com/@schimmelautomobile" legacyBehavior>
                <IconButton
                  aria-label="TikTok"
                  color="secondary"
                  component="a"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                >
                  <SiTiktok style={{
                    width: 20,
                    height: 20,
                  }}
                  />
                </IconButton>
              </Link>
            </Box>
          </Grid2>
          {columns.map((column) => (
            <Grid2
              key={column.id}
              className={column.cssClass}
              size={{ xs: 12, sm: 6, md: 'grow' }}
            >
              <Typography
                color="secondary"
                component="div"
                sx={{
                  mb: 3,
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center',
                  '& svg': {
                    width: 20,
                    height: 20,
                    marginRight: theme.spacing(1),
                  },
                  transform: `translateX(-${theme.spacing(1) + 20}px)`,
                }}
                variant="h6"
              >
                {column.title}
              </Typography>
              <FooterMenu
                items={column.items ?? []}
                onChangeConsent={handleChangeConsent}
              />
            </Grid2>
          ))}
        </Grid2>
        <Box
          sx={{
            ...SEPARATOR_STYLE,
          }}
        />
        <Box
          mt={3}
          sx={{
            color: theme.palette.primary.contrastText,
            opacity: 0.65,
          }}
        >
          <Typography color="dark.contrastText" component="p" variant="caption">
            &copy;
            {' '}
            {DateTime.local().year}
            {' '}
            {t('components.organisms.Footer.description')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

const SEPARATOR_STYLE = {
  borderBottomWidth: 2,
  borderBottomStyle: 'solid',
  borderBottomColor: 'rgba(255,255,255,0.1)',
};
